import React, { useState } from "react";
import { css } from "@emotion/css";
import { Popover } from "react-text-selection-popover";

/**
 * Floats a small action bar above highlighted lesson/explanation text:
 * **Copy** and **Dive deeper**.
 *
 * Ported from boilit's MarkdownWithHighlightPowers: it drives the
 * ``react-text-selection-popover`` library (a thin reader over the native
 * Selection API) and renders the bar as a fixed, well-above-the-selection
 * control that swallows touch/mouse-down + ``touch-action: none`` +
 * ``-webkit-touch-callout: none``.  That combination keeps the OS
 * copy/share callout from popping up over our bar on mobile browsers — so
 * we provide **Copy** ourselves to keep clipboard access seamless.
 */

const MIN_LEN = 2;
const MAX_LEN = 120;
// Small gap directly above the selection.  On iOS the OS edit menu sits
// BELOW the selection, so our bar reads best snug above it (not shifted
// far up); the touch-handling below — not distance — is what keeps the OS
// callout from colliding with our bar.
const ABOVE_PX = 8;

// The wrapper must not disturb the parent's (flex) layout — it only scopes
// selection detection to its subtree.
const hostStyle = css`
  display: contents;
  -webkit-touch-callout: none;
`;

const barStyle = css`
  position: fixed;
  z-index: 1100;
  transform: translateX(-50%) translateY(-100%);
  display: inline-flex;
  align-items: stretch;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  overflow: hidden;
  pointer-events: auto;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  box-shadow: 0 6px 18px -6px var(--color-shadow-strong, rgba(0, 0, 0, 0.5));

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -5px;
    width: 10px;
    height: 10px;
    background: var(--color-bg-elevated);
    border-right: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    transform: translateX(-50%) rotate(45deg);
  }
`;

const actionStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: var(--color-text);
  font-size: 0.82em;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:hover { background: var(--color-bg-subtle); }
  &:active { background: var(--color-bg-raised); }
`;

const diveStyle = css`
  ${actionStyle};
  color: var(--color-primary);
`;

const dividerStyle = css`
  width: 1px;
  background: var(--color-border-subtle);
  flex-shrink: 0;
`;

interface SelectionPopoverProps {
  /** Called with the highlighted text when "Dive deeper" is tapped. */
  onDiveDeeper: (text: string) => void;
  /** When true, selection never surfaces the bar (e.g. while streaming). */
  disabled?: boolean;
  children: React.ReactNode;
}

export const SelectionPopover: React.FC<SelectionPopoverProps> = ({
  onDiveDeeper,
  disabled,
  children,
}) => {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);

  const swallow = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const clearSelection = () => window.getSelection()?.removeAllRanges();

  const handleCopy = async (e: React.MouseEvent, text: string) => {
    swallow(e);
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      /* fall through to execCommand */
    }
    if (!ok) {
      // The text is still selected (we swallowed mousedown), so this copies it.
      try {
        ok = document.execCommand("copy");
      } catch {
        /* give up silently */
      }
    }
    setCopied(true);
    window.setTimeout(() => {
      setCopied(false);
      clearSelection();
    }, 900);
  };

  const handleDive = (e: React.MouseEvent, text: string) => {
    swallow(e);
    onDiveDeeper(text);
    clearSelection();
  };

  // Rendered by the library as a component, receiving the live selection.
  const renderBar = ({
    clientRect,
    isCollapsed,
    textContent,
  }: {
    clientRect?: { left: number; top: number; width: number } | null;
    isCollapsed?: boolean;
    textContent?: string;
  }) => {
    if (!clientRect || isCollapsed) return null;
    const text = (textContent || "").trim();
    if (text.length < MIN_LEN || text.length > MAX_LEN) return null;
    return (
      <div
        className={barStyle}
        style={{
          left: clientRect.left + clientRect.width / 2,
          top: clientRect.top - ABOVE_PX,
        }}
        onMouseDown={swallow}
        onTouchStart={swallow}
        data-testid="selection-popover"
      >
        <button
          type="button"
          className={actionStyle}
          onMouseDown={swallow}
          onTouchStart={swallow}
          onClick={(e) => handleCopy(e, text)}
          data-testid="selection-copy"
        >
          <span aria-hidden="true">{copied ? "✓" : "📋"}</span>
          {copied ? "Copied" : "Copy"}
        </button>
        <span className={dividerStyle} aria-hidden="true" />
        <button
          type="button"
          className={diveStyle}
          onMouseDown={swallow}
          onTouchStart={swallow}
          onClick={(e) => handleDive(e, text)}
          data-testid="selection-dive-deeper"
        >
          <span aria-hidden="true">🔎</span>
          Dive deeper
        </button>
      </div>
    );
  };

  return (
    <>
      <div ref={setHost} className={hostStyle} data-testid="selection-host">
        {children}
      </div>
      {host && !disabled && (
        <Popover target={host} mount={document.body} render={renderBar} />
      )}
    </>
  );
};
